import {Box, CircularProgress, Menu, MenuItem, styled} from '@mui/material'
import {useState} from 'react'
import {StyledSaveAndGenerateButton} from '../../Styles'
import CustomDialog from '../../../../Common/CustomDialog'
import {
  StyledActionAddButton,
  StyledActionResetButton,
  StyledInterestTagTitleContainer
} from '../../../AskGivePrism/Styles'
import {StyledActionBox} from '../../../../Common/Styles'
import ExportListDialog, {columnNameOptions} from './ExportListDialog'
import useExportList from '../../../../../api/exportList'
import {useSelector, useDispatch} from 'react-redux'
import PropTypes from 'prop-types'
import paginationLimit from '../../../../../configs/paginationLimit.json'
import {
  setExportError,
  setExportSuccess,
  setIsExporting
} from '../../../../../store/prospectSegment/exportList/exportListSlice'

export const StyledMenu = styled(Menu)(({theme}) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.common.grey,
    minWidth: '150px'
  },
  '& .MuiMenuItem-root': {
    ':hover': {
      backgroundColor: theme.palette.common.grey2
    }
  }
}))

const menuOptions = [
  {label: 'CSV', key: 'csv'},
  {label: 'XLSX', key: 'xlsx'}
]

const ExportListIndex = ({disableExport, searchTerm, count, constituentsList, documentName, listName}) => {
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedFileType, setSelectedFileType] = useState('csv')
  const isExporting = useSelector((state) => state.exportList.isExporting)
  const [latestDocumentName, setLatestDocumentName] = useState(documentName)
  const exportLimit = paginationLimit.pagination.prospectsSegment
  const [formData, setFormData] = useState({
    fileType: 'csv',
    documentName,
    pageRange: '',
    limit: exportLimit,
    selectedColumns: columnNameOptions.map((option) => option.value),
    isValid: false
  })
  const openExportMenu = Boolean(anchorEl)
  const azureRole = useSelector((state) => state.superUserTenantSlice.azureRole)
  const askGivePrismFilters = useSelector((state) => state.askGivePrism.filters) || {}
  const sortDirection = useSelector((state) => state.getAllProspectsData.sortDirection)
  const sortColumn = useSelector((state) => state.getAllProspectsData.sortColumn)
  const hasMainPrompt = useSelector((state) => state.chatBotSlice.message)

  const handleDownloadError = (error) => {
    dispatch(setExportError(error))
  }

  const handleDownloadComplete = () => {
    dispatch(setIsExporting(false))
    dispatch(setExportSuccess(true))
    setOpenDialog(false)
    setFormData({
      fileType: 'csv',
      documentName: '',
      limit: exportLimit,
      pageRange: '',
      isValid: false,
      selectedColumns: columnNameOptions.map((option) => option.value)
    })
  }

  const [exportList] = useExportList(handleDownloadError, handleDownloadComplete, latestDocumentName)

  const handleExportBtnClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportMenuClose = () => {
    setAnchorEl(null)
  }

  const handleExportListClick = ({selectedMenuOption}) => {
    setOpenDialog(true)
    setSelectedFileType(selectedMenuOption)
  }

  const handleExportListClose = () => {
    setOpenDialog(false)
    setFormData({
      fileType: 'csv',
      documentName: '',
      pageRange: '',
      limit: exportLimit,
      isValid: false,
      selectedColumns: columnNameOptions.map((option) => option.value)
    })
  }

  const getFilterValues = () => {
    if (constituentsList.length > 0) {
      return {uniqueConstituentIdentifier: constituentsList}
    } else {
      return {name: searchTerm, ...askGivePrismFilters}
    }
  }

  const getStartIndex = () => {
    if (formData.pageRange) {
      const [start] = formData.pageRange.split('-').map(Number)
      return (start - 1) * exportLimit
    } else {
      return 0
    }
  }

  const getLimit = () => {
    if (constituentsList.length > 0) {
      return constituentsList.length
    } else if (formData.pageRange) {
      const [start, end] = formData.pageRange.split('-').map(Number)
      if (!end) {
        return exportLimit
      }
      return (end - start + 1) * exportLimit
    } else {
      return count
    }
  }

  const handleExportListConfirm = () => {
    if (!formData.isValid) {
      return
    }

    setLatestDocumentName(formData.documentName) // Update the latest document name for email export

    dispatch(setIsExporting(true))
    exportList({
      variables: {
        documentName: formData.documentName,
        fileType: formData.fileType,
        limit: getLimit(),
        filters: getFilterValues(),
        startIndex: getStartIndex(),
        selectedColumns: formData.selectedColumns,
        filteredList: !!hasMainPrompt,
        listName,
        ...(azureRole ? {azureRole} : {}),
        ...(sortColumn ? {sortColumn} : {}),
        ...(sortDirection ? {sortDirection} : {})
      }
    })
    setOpenDialog(false)
    setFormData({
      fileType: 'csv',
      documentName: '',
      limit: exportLimit,
      pageRange: '',
      isValid: false,
      selectedColumns: columnNameOptions.map((option) => option.value)
    })
  }

  return (
    <>
      <Box>
        <StyledSaveAndGenerateButton
          variant="outlined"
          onClick={handleExportBtnClick}
          disabled={count === 0 || isExporting || disableExport}
        >
          Export List{isExporting && <CircularProgress sx={{color: 'grey', ml: 1}} size={16} />}
        </StyledSaveAndGenerateButton>
        <Menu anchorEl={anchorEl} open={openExportMenu} onClose={handleExportMenuClose} onClick={handleExportMenuClose}>
          {menuOptions.map((option) => (
            <MenuItem key={option.key} onClick={() => handleExportListClick({selectedMenuOption: option.key})} dense>
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <CustomDialog
        open={openDialog}
        maxWidth="md"
        timelineDialog={true}
        showDivider={true}
        showCloseBtn={true}
        handleClose={handleExportListClose}
        title={<StyledInterestTagTitleContainer sx={{padding: 0}}>{'Export Settings'}</StyledInterestTagTitleContainer>}
        content={
          <ExportListDialog
            count={count}
            onFormDataChange={(data) => setFormData(data)}
            fileType={selectedFileType}
            constituentsList={constituentsList}
            documentName={documentName}
          />
        }
        actions={
          <StyledActionBox>
            <StyledActionResetButton onClick={handleExportListClose}>Cancel</StyledActionResetButton>
            <StyledActionAddButton onClick={handleExportListConfirm} disabled={!formData.isValid}>
              Confirm
            </StyledActionAddButton>
          </StyledActionBox>
        }
      />
    </>
  )
}

ExportListIndex.propTypes = {
  count: PropTypes.number.isRequired,
  constituentsList: PropTypes.array.isRequired,
  documentName: PropTypes.string.isRequired,
  listName: PropTypes.string,
  disableExport: PropTypes.bool,
  searchTerm: PropTypes.string
}

export default ExportListIndex
